
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import html2canvas from 'html2canvas';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import * as turf from '@turf/turf';

import './App.css';
import './Custom.css';

import { MobileView, isMobile } from 'react-device-detect';
import { useNavigate, useLocation } from 'react-router-dom';

// icons
import { MdAccountCircle } from "react-icons/md";
import { FaDrawPolygon } from "react-icons/fa";
import { TbLine } from "react-icons/tb";
import { GoDotFill } from "react-icons/go";

// draw
import ResetMap from './components/core/resetMap.js';
import initializeMapboxDraw from './components/core/initializeMapboxDraw.js';
import ResetMapToOriginalData from './components/core/resetMapToOriginalData.js';

// ui
import TopMenu from './components/ui/menus/topMenu.js';
import LoadingSpinner from './components/ui/modals/loadingSpinnerModal.js';
import ClickedInfoSummary from './components/ui/modals/ClickedInfoSummary.js';
import PDFConverterModel from './components/ui/modals/pdfConverterModel.js';
import TopSubMenu from './components/ui/menus/topSubMenu.js';
import LeftMenu from './components/ui/menus/leftMenu.js';
import LeftSubMenu from './components/ui/menus/leftSubMenu.js';
import ComparePanel from './components/ui/menus/leftSubMenuComponents/comparePanel.js';
import FilterFeatureDataSimpleTimeseriesModal from './components/ui/modals/filterFeatureDataSimpleTimeseriesModal.js';
import FilterFeatureDataSimpleModal from './components/ui/modals/filterFeatureDataSimpleModal.js';
import DataLoad from './components/dataManagement/dataLoad.js';
import SettingsPanel from './components/ui/menus/leftSubMenuComponents/settingsPanel.js';
import AboutPanel from './components/ui/staticPages/about.js';
import PrivacyPanel from './components/ui/staticPages/privacy.js';
import TacPanel from './components/ui/staticPages/termsandconditions.js';
import InvestorsPanel from './components/ui/staticPages/investorsPanel.js';
import LargePieChart from './components/ui/menus/leftSubMenuComponents/largePieChart.js';
import LoadMenuModal from './components/ui/modals/loadMenuModal.js';
import ResetLandDataConfirmationModal from './components/ui/modals/resetLandDataConfirmationModal.js';
import DynamicStringMetricKey from './components/ui/modals/dynamicStringMetricKey.js';
import CommsToAPI from './components/ui/modals/commsToAPI.js';

// auth
import CreateAccountPopup from './components/ui/modals/createAccountPopup.js';

import {
  loading_timer_count,
  useGlobalRefs,
  useGlobalFileStates,
  useGlobalState,
  fmesh,
  fmeshPoints
} from './components/core/globalVariables.js';

import logo from './components/images/logo.png';

import useFeatureMarkers from './components/ui/menus/leftMenuComponents/clickedPolygonMarkers.js';
import useScenarioModellingMarkers from './components/ui/menus/leftMenuComponents/clickedPolygonMarkersSM.js';

const HomeApp = () => {

  const globalRefs = useGlobalRefs();
  const globalFileStates = useGlobalFileStates();
  const globalState = useGlobalState();
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('harrier-dark-mode') === 'true');
  const [hoveredFeatureTooltip, setHoveredFeatureTooltip] = useState(null);
  const hoverTooltipElementRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('harrier-dark-mode', String(isDarkMode));
    window.dispatchEvent(new CustomEvent('harrier-theme-change', { detail: { isDarkMode } }));
  }, [isDarkMode]);

  let mapRef = globalRefs.mapRef;
  let drawRef = globalRefs.drawRef;
  let polygonCustomLayerRef = globalRefs.polygonCustomLayerRef;
  let longstandingGeoJSONRef = globalRefs.longstandingGeoJSONRef;
  let mapContainerRef = globalRefs.mapContainerRef;
  let originalGeoJSONforComparingRef = globalRefs.originalGeoJSONforComparingRef;
  let mapboxTokenRef = globalRefs.mapboxTokenRef;
  let extendedpiechartRef = globalRefs.extendedpiechartRef;
  let piechartRef = globalRefs.piechartRef;
  let PDFpiechartRef = globalRefs.PDFpiechartRef;
  let pendingBoundaryChangesRef = globalRefs.pendingBoundaryChangesRef;
  let currentMapIdRef = globalRefs.currentMapIdRef;

  let workingLocally = globalState.workingLocally;
  let devDeployment = globalState.devDeployment;

  let isFilenameInputOpen = globalFileStates.isFilenameInputOpen;
  let setIsFilenameInputOpen = globalFileStates.setIsFilenameInputOpen;
  let newFilename = globalFileStates.newFilename;
  let setNewFilename = globalFileStates.setNewFilename;
  let setNewFileScreenShot = globalFileStates.setNewFileScreenShot;
  let loadedFileName = globalFileStates.loadedFileName;

  let clickedInfo = globalState.clickedInfo;
  let clickedInfoHeader = globalState.clickedInfoHeader;
  let clickedInfoHeaderMobile = globalState.clickedInfoHeaderMobile;
  let isVisible = globalState.isVisible;
  let setIsVisible = globalState.setIsVisible;
  let isDrawing = globalState.isDrawing;
  let setisDrawing = globalState.setisDrawing;
  let isLoading = globalState.isLoading;
  let setIsLoading = globalState.setIsLoading;
  let filenames = globalState.filenames;
  let isMenuLoadClick = globalState.isMenuLoadClick;
  let setisMenuLoadClick = globalState.setisMenuLoadClick;
  let isHasSaved = globalState.isHasSaved;
  let isHasSavedCurrentData = globalState.isHasSavedCurrentData;
  let openAuth = globalState.openAuth;
  let setopenAuth = globalState.setopenAuth;
  let isSelectingLandtypes = globalState.isSelectingLandtypes;
  let setisSelectingLandtypes = globalState.setisSelectingLandtypes;
  let isLeftDrawerOpen = globalState.isLeftDrawerOpen;
  let setisLeftDrawerOpen = globalState.setisLeftDrawerOpen;
  let isLeftSubDrawerOpen = globalState.isLeftSubDrawerOpen;
  let setisLeftSubDrawerOpen = globalState.setisLeftSubDrawerOpen;
  let selectedLandTypes = globalState.selectedLandTypes;
  let setSelectedLandTypes = globalState.setSelectedLandTypes;
  let selectedMetric = globalState.selectedMetric;
  let selectedKey = globalState.selectedKey;
  let setSelectedMetric = globalState.setSelectedMetric;
  let isViewingPieChart = globalState.isViewingPieChart;
  let setisViewingPieChart = globalState.setisViewingPieChart;
  let isViewingForecast = globalState.isViewingForecast;
  let setisViewingForecast = globalState.setisViewingForecast;
  let selectedYearEditing = globalState.selectedYearEditing;
  let setSelectedYearEditing = globalState.setSelectedYearEditing;
  let isModifying = globalState.isModifying;
  let setisModifying = globalState.setisModifying;
  let isComparing = globalState.isComparing;
  let setisComparing = globalState.setisComparing;
  let isInSettings = globalState.isInSettings;
  let setIsInSettings = globalState.setIsInSettings;
  let isInAboutPanel = globalState.isInAboutPanel;
  let setIsInAboutPanel = globalState.setIsInAboutPanel;
  let isInPrivacyPanel = globalState.isInPrivacyPanel;
  let setIsInPrivacyPanel = globalState.setIsInPrivacyPanel;
  let isInTacPanel = globalState.isInTacPanel;
  let setIsInTacPanel = globalState.setIsInTacPanel;
  let isInInvestorsPanel = globalState.isInInvestorsPanel;
  let setIsInInvestorsPanel = globalState.setIsInInvestorsPanel;
  let dropdownhighlightedIndex = globalState.dropdownhighlightedIndex;
  let setdropdownhighlightedIndex = globalState.setdropdownhighlightedIndex;
  let setIs2DViewActive = globalState.setIs2DViewActive;
  let uniqueTextureNamesArray = globalState.uniqueTextureNamesArray;
  let mapbearing = globalState.mapbearing;
  let setMapBearing = globalState.setMapBearing;
  let isLanduse = globalState.isLanduse;
  let setIsLanduse = globalState.setIsLanduse;
  let isViewingLargePieChart = globalState.isViewingLargePieChart;
  let setisViewingLargePieChart = globalState.setisViewingLargePieChart;
  let comparingLandtypeSelectedOption = globalState.comparingLandtypeSelectedOption;
  let setcomparingLandtypeSelectedOption = globalState.setcomparingLandtypeSelectedOption;
  let isDraggableSubDrawerOpen = globalState.isDraggableSubDrawerOpen;
  let setisDraggableSubDrawerOpen = globalState.setisDraggableSubDrawerOpen;
  let storedfeaturecurrentlandtype = globalState.storedfeaturecurrentlandtype;
  let setStoredFeatureCurrentLandtype = globalState.setStoredFeatureCurrentLandtype;
  let setIsCtrlDown = globalState.setIsCtrlDown;
  let loadedAmount = globalState.loadedAmount;
  let setLoadedAmount = globalState.setLoadedAmount;
  let isControlsNavOpen = globalState.isControlsNavOpen;
  let setIsControlsNavOpen = globalState.setIsControlsNavOpen;
  let foundFeature = globalState.foundFeature;
  let loggedInToAPI = globalState.loggedInToAPI;
  let setLoggedInToAPI = globalState.setLoggedInToAPI;
  let adminUser = globalState.adminUser;
  let setAdminUser = globalState.setAdminUser;
  let mapLoaded = globalState.mapLoaded;
  let setMapLoaded = globalState.setMapLoaded;
  let loadedPercentage = globalState.loadedPercentage;
  let setLoadedPercentage = globalState.setLoadedPercentage;
  let temporaryGeoJSONIntendedForSaving = globalState.temporaryGeoJSONIntendedForSaving;
  let setTemporaryGeoJSONIntendedForSaving = globalState.setTemporaryGeoJSONIntendedForSaving;
  let finishedEditing = globalState.finishedEditing;
  let setFinishedEditing = globalState.setFinishedEditing;
  let hasChangedKeyValue = globalState.hasChangedKeyValue;
  let setHasChangedKeyValue = globalState.setHasChangedKeyValue;
  let compareSelectedKey = globalState.compareSelectedKey;
  let setCompareSelectedKey = globalState.setCompareSelectedKey;
  let landKeySelectedKey = globalState.landKeySelectedKey;
  let setLandKeySelectedKey = globalState.setLandKeySelectedKey;
  let convertingData = globalState.convertingData;
  let universalDataLoaded = globalState.universalDataLoaded;
  let setUniversalDataLoaded = globalState.setUniversalDataLoaded;
  let globalPolygonTransparency = globalState.globalPolygonTransparency;
  let setGlobalPolygonTransparency = globalState.setGlobalPolygonTransparency;
  let isResetLandDataConfirmationModalOpen = globalState.isResetLandDataConfirmationModalOpen;
  let setIsResetLandDataConfirmationModalOpen = globalState.setIsResetLandDataConfirmationModalOpen;
  let storeEditedBoundaryFeatureSkylarkID = globalState.storeEditedBoundaryFeatureSkylarkID;
  let setStoreEditedBoundaryFeatureSkylarkID = globalState.setStoreEditedBoundaryFeatureSkylarkID;
  let isCreatingPDF = globalState.isCreatingPDF;
  let setIsCreatingPDF = globalState.setIsCreatingPDF;
  let layers = globalState.layers;
  let setLayers = globalState.setLayers;
  let activeLayer = globalState.activeLayer;
  let setActiveLayer = globalState.setActiveLayer;
  let showFilterFeatureDataSimpleTimeseriesModal = globalState.showFilterFeatureDataSimpleTimeseriesModal;
  let setShowFilterFeatureDataSimpleTimeseriesModal = globalState.setShowFilterFeatureDataSimpleTimeseriesModal;
  let showFilterFeatureDataSimpleModal = globalState.showFilterFeatureDataSimpleModal;
  let setShowFilterFeatureDataSimpleModal = globalState.setShowFilterFeatureDataSimpleModal;
  let IsLoadingNewGeoJSONFromAnUpdate = globalState.IsLoadingNewGeoJSONFromAnUpdate;
  let setIsLoadingNewGeoJSONFromAnUpdate = globalState.setIsLoadingNewGeoJSONFromAnUpdate;
  let APIReportMessage = globalState.APIReportMessage;
  let setAPIReportMessage = globalState.setAPIReportMessage;

  // - - - - -

  let universalParcelDifferentiator = globalState.universalParcelDifferentiator;
  let universalColorPaletteName = globalState.universalColorPaletteName;

  let selectedFirstPrivateMapParentRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const toastRef = globalRefs.toastRef;

  let setUserEmail = globalState.setUserEmail;
  let userID = globalState.userID;
  let setUserID = globalState.setUserID;
  const setGlobalPublicSelectedDataset = globalState.setGlobalPublicSelectedDataset;

  // - - - - -

  let LOADINGPARAM_startOfLoad = globalState.LOADINGPARAM_startOfLoad;
  let setLOADINGPARAM_startOfLoad = globalState.setLOADINGPARAM_startOfLoad;

  let LOADINGPARAM_makeScene = globalState.LOADINGPARAM_makeScene;
  let setLOADINGPARAM_makeScene = globalState.setLOADINGPARAM_makeScene;

  let LOADINGPARAM_initAnimation = globalState.LOADINGPARAM_initAnimation;
  let setLOADINGPARAM_initAnimation = globalState.setLOADINGPARAM_initAnimation;

  let LOADINGPARAM_onAdd = globalState.LOADINGPARAM_onAdd;
  let setLOADINGPARAM_onAdd = globalState.setLOADINGPARAM_onAdd;

  let LOADINGPARAM_createShapes = globalState.LOADINGPARAM_createShapes;
  let setLOADINGPARAM_createShapes = globalState.setLOADINGPARAM_createShapes;

  let LOADINGPARAM_applyDateRange = globalState.LOADINGPARAM_applyDateRange;
  let setLOADINGPARAM_applyDateRange = globalState.setLOADINGPARAM_applyDateRange;

  let LOADINGPARAM_resetMapAfterDatesReceived = globalState.LOADINGPARAM_resetMapAfterDatesReceived;
  let setLOADINGPARAM_resetMapAfterDatesReceived = globalState.setLOADINGPARAM_resetMapAfterDatesReceived;

  let LOADINGPARAM_fetchKeyData = globalState.LOADINGPARAM_fetchKeyData;
  let setLOADINGPARAM_fetchKeyData = globalState.setLOADINGPARAM_fetchKeyData;

  let LOADINGPARAM_fetchNumericData = globalState.LOADINGPARAM_fetchNumericData;
  let setLOADINGPARAM_fetchNumericData = globalState.setLOADINGPARAM_fetchNumericData;

  let multuGroupSelectedArray = globalState.multuGroupSelectedArray;
  let setMultuGroupSelectedArray = globalState.setMultuGroupSelectedArray;

  let featureList = globalState.featureList;

  const setSMSelectedArray = globalState.setSMSelectedArray;
  const SMSelectedArray = globalState.SMSelectedArray;

  // - - - - -

  useEffect(() => {
    if (
      LOADINGPARAM_startOfLoad === true &&
      LOADINGPARAM_makeScene === true &&
      LOADINGPARAM_initAnimation === true &&
      LOADINGPARAM_onAdd === true &&
      LOADINGPARAM_createShapes === true &&
      LOADINGPARAM_applyDateRange === true &&
      LOADINGPARAM_resetMapAfterDatesReceived === true &&
      LOADINGPARAM_fetchKeyData === true &&
      LOADINGPARAM_fetchNumericData === true
    ) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [
    LOADINGPARAM_startOfLoad,
    LOADINGPARAM_makeScene,
    LOADINGPARAM_initAnimation,
    LOADINGPARAM_onAdd,
    LOADINGPARAM_createShapes,
    LOADINGPARAM_applyDateRange,
    LOADINGPARAM_resetMapAfterDatesReceived,
    LOADINGPARAM_fetchKeyData,
    LOADINGPARAM_fetchNumericData
  ]);

  const previousScenarioMapViewRef = useRef(globalState.scenarioModelledViewEnabled);
  const previousScenarioModelsRef = useRef(globalState.scenarioModels);
  useEffect(() => {
    const viewChanged = previousScenarioMapViewRef.current !== globalState.scenarioModelledViewEnabled;
    const modelsChanged = previousScenarioModelsRef.current !== globalState.scenarioModels;
    previousScenarioMapViewRef.current = globalState.scenarioModelledViewEnabled;
    previousScenarioModelsRef.current = globalState.scenarioModels;
    if (!viewChanged && !(modelsChanged && globalState.scenarioModelledViewEnabled)) return;
    if (!mapRef.current || !polygonCustomLayerRef.current || !longstandingGeoJSONRef.current) return;
    ResetMap(globalRefs, globalState);
  }, [globalState.scenarioModelledViewEnabled, globalState.scenarioModels]);

  // 

  useFeatureMarkers({
    mapRef,
    longstandingGeoJSONRef,
    multuGroupSelectedArray,
  });

  useScenarioModellingMarkers({
    mapRef,
    longstandingGeoJSONRef,
    SMSelectedArray,
    scenarioHighlightedFeatureIds: globalState.scenarioHighlightedFeatureIds,
  });

  useEffect(() => {
    if (polygonCustomLayerRef.current && fmesh) {
      if (multuGroupSelectedArray.length === 0) {
        fmesh.traverse(child => {
          if (child.isMesh) {
            child.material.opacity = 1;
            child.material.transparent = false;
            child.material.needsUpdate = true;
          }
        });

        polygonCustomLayerRef.current.map_repaint();
      } else {
        polygonCustomLayerRef.current.adjustFeatureOpacities(
          fmesh,
          multuGroupSelectedArray
        );
      }
    }
  }, [multuGroupSelectedArray]);

  // Function to handle filename input change

  const handleFilenameInputChange = (e) => {
    setNewFilename(e.target.value);
  };

  // Load data from server

  const closeLoadMenu = () => {
    setisMenuLoadClick(false);
  }

  // Load data from server

  const loadMenuClick = () => {
    setisMenuLoadClick((prevIsMenuLoadClick) => !prevIsMenuLoadClick);
  }

  // Auth clicked

  const authClicked = () => {
    setopenAuth(true);
  };

  // Close auth

  const closeAuth = () => {
    setopenAuth(false);
  }

  // Landtype selection cancel on click

  const LandtypeSelectionCancelOnClick = () => {
    if (isSelectingLandtypes) {
      resetLeftSubMenu();
      setisDraggableSubDrawerOpen(false);
      setisSelectingLandtypes(false);
      setisLeftSubDrawerOpen(false);
    } else {
      if (isMobile) {
        resetLeftSubMenu();
        setisDraggableSubDrawerOpen(true);
        setisSelectingLandtypes(true);
      } else {
        resetLeftSubMenu();
        setisSelectingLandtypes(true);
        setisLeftSubDrawerOpen(true);
      }
    }
  }

  const PieChartselectionCancelOnClick = () => {
    if (isViewingPieChart) {
      resetLeftSubMenu();
      setisDraggableSubDrawerOpen(false);
      setisViewingPieChart(false);
      setisLeftSubDrawerOpen(false);
    } else {
      if (isMobile) {
        resetLeftSubMenu();
        setisDraggableSubDrawerOpen(true);
        setisViewingPieChart(true);
      } else {
        resetLeftSubMenu();
        setisViewingPieChart(true);
        setisLeftSubDrawerOpen(true);
      }
    }
  }

  const handleEnlargePieButtonClick = () => {
    setisViewingLargePieChart(true);
    setisViewingPieChart(false);
    setisLeftSubDrawerOpen(false);
    polygonCustomLayerRef.current.resetfeatureOpacities(fmesh);
  }

  const handleClosePieButtonClick = () => {
    setisViewingLargePieChart(false);
    setisViewingPieChart(false);
    setisLeftSubDrawerOpen(false);
  }

  const ForecastCancelOnClick = () => {
    resetLeftSubMenu();
    setisViewingForecast(true);
    setisLeftSubDrawerOpen(true);
  }

  const ModifySelectionCancelOnClick = () => {
    if (isViewingForecast) {
      resetLeftSubMenu();
      setisDraggableSubDrawerOpen(false);
      setisViewingForecast(false);
      setisLeftSubDrawerOpen(false);
    } else {
      setisViewingForecast(true);
      resetLeftSubMenu();
      setisLeftSubDrawerOpen(true);
    }
  }

  const handleModifyClick = () => {
    if (isModifying) {
      setIs2DViewActive(false);
      resetLeftSubMenu();
      setisDraggableSubDrawerOpen(false);
      setisModifying(false);
      setisLeftSubDrawerOpen(false);
    } else {
      setIs2DViewActive(true);
      resetLeftSubMenu();
      setisModifying(true);
      beginDrawOnClick();
      setisLeftSubDrawerOpen(true);
    }
  }

  const handleCompareClick = () => {
    if (isComparing) {
      resetLeftSubMenu();
      setisDraggableSubDrawerOpen(false);
      setisComparing(false);
      setisLeftSubDrawerOpen(false);
    } else {
      resetLeftSubMenu();
      setisComparing(true);
      setisLeftSubDrawerOpen(true);
    }
  }

  const handleLandUseClick = () => {
    if (isLanduse) {
      resetLeftSubMenu();
      setisDraggableSubDrawerOpen(false);
      setIsLanduse(false);
      setisLeftSubDrawerOpen(false);
    } else {
      resetLeftSubMenu();
      setIsLanduse(true);
      setisLeftSubDrawerOpen(true);
    }
  }

  // Begin draw

  const beginDrawOnClick = () => {
    setIs2DViewActive(true);
    polygonCustomLayerRef.current.setCameraTo2DView();
    setisDrawing(true);
    setisLeftSubDrawerOpen(true);
    setTimeout(() => {
      initializeMapboxDraw(globalRefs, globalState);
      polygonCustomLayerRef.current.removeDrawFromScene();
    }, loading_timer_count);
  };

  // End draw and save

  const endDrawOnClick = () => {
    setIs2DViewActive(false);
    polygonCustomLayerRef.current.setCameraTo3DView(selectedYearEditing);
    setisDrawing(false);
    setisModifying(false);
    setIs2DViewActive(false);
    setisLeftSubDrawerOpen(false);
    setFinishedEditing(false);
    polygonCustomLayerRef.current.handleRecalculateValues(storeEditedBoundaryFeatureSkylarkID);
    ResetMap(globalRefs, globalState);
  };

  // End draw and cancel

  const endDrawCancelOnClick = () => {
    pendingBoundaryChangesRef.current = [];
    setIs2DViewActive(false);
    polygonCustomLayerRef.current.setCameraTo3DView(selectedYearEditing);
    setisDrawing(false);
    setisModifying(false);
    ResetMap(globalRefs, globalState);
    setisLeftSubDrawerOpen(false);
    setFinishedEditing(false);
  };

  // Handle landtype p tag click

  const handleLandtypePTagClick = (item, selectedKey) => {
    if (item === 'Reset') {
      polygonCustomLayerRef.current.setLandtype([]);
      setSelectedLandTypes([]);
      return;
    }
    setSelectedLandTypes(prevSelectedLandTypes => {
      if (prevSelectedLandTypes.includes(item)) {
        const newSelectedLandTypes = prevSelectedLandTypes.filter(type => type !== item);
        polygonCustomLayerRef.current.setLandtype(newSelectedLandTypes, selectedKey);
        return newSelectedLandTypes
      } else {
        const newSelectedLandTypes = [...prevSelectedLandTypes, item];
        polygonCustomLayerRef.current.setLandtype(newSelectedLandTypes, selectedKey);
        return newSelectedLandTypes;
      }
    });
  };

  const handleLandtypePieHover = (item, selectedKey) => {
    if (item === 'Reset') {
      polygonCustomLayerRef.current.setPieLandtype([]);
      setSelectedLandTypes([]);
      return;
    }
    setSelectedLandTypes([item]);
    polygonCustomLayerRef.current.setPieLandtype([item], selectedKey);
  };

  // Reset left sub menu

  const resetLeftSubMenu = () => {
    setSelectedLandTypes([]);
    if (isDrawing) {
      ResetMap(globalRefs, globalState);
      setisDrawing(false);
    }
    if (isSelectingLandtypes) {
      setisSelectingLandtypes(false);
      setSelectedLandTypes([]);
    }
    if (isViewingPieChart) {
      setisViewingPieChart(false);
    }
    if (extendedpiechartRef.current) {
      extendedpiechartRef.current.destroy();
    }
    if (isViewingForecast) {
      setisViewingForecast(false);
    }
    if (isModifying) {
      setisModifying(false);
    }
    if (isComparing) {
      setisComparing(false);
    }
    if (isLanduse) {
      setIsLanduse(false);
    }
  }

  const toggleDrawer = () => {
    setisLeftDrawerOpen((prevState) => !prevState);
  }

  const toggleLeftSubDrawer = () => {
    if (isModifying) {
      setIs2DViewActive(false);
      polygonCustomLayerRef.current.setCameraTo3DView(selectedYearEditing);
    }
    setisLeftSubDrawerOpen((prevState) => !prevState);
    resetLeftSubMenu();
  }

  const isStateUpdated = useRef(false);

  const closeSettingsPanel = () => {
    setIsInSettings(false);
  }

  const openAboutPanel = () => {
    window.location.href = 'https://harrier.land';
  }

  const openPrivacyPanel = () => {
    resetAboutPanels();
    setIsInPrivacyPanel(true);
  }

  const openTacPanel = () => {
    resetAboutPanels();
    setIsInTacPanel(true);
  }

  const openInverstorsPanel = () => {
    resetAboutPanels();
    setIsInInvestorsPanel(true);
  }

  const openMobileSignIn = () => {
    setopenAuth(true);
  }

  const resetAboutPanels = () => {
    setIsInSettings(false);
    setIsInAboutPanel(false);
    setIsInPrivacyPanel(false);
    setIsInTacPanel(false);
    setIsInInvestorsPanel(false);
  }
  // Initialize map
  const initializeMap = () => {
    mapboxgl.accessToken = mapboxTokenRef.current;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/jevonmahoney/clye79kxt00oz01pmchxfdgvq',
      center: [-0.9061603891426653, 54.121878725877096],
      zoom: 5,
      pitch: 60,
      preserveDrawingBuffer: true
    });

    mapRef.current.on('load', () => {
      if (mapRef.current.isStyleLoaded()) {
        setMapLoaded(true);
        setLoadedPercentage(20);
        mapRef.current.setPaintProperty(
          'mapbox-satellite',
          'raster-opacity',
          0 
        );
      }
    });

    mapRef.current.on("load", () => {
      drawRef.current = new MapboxDraw({
        displayControlsDefault: true,
        controls: {
          polygon: true,
          trash: true,
        },
      });
    });

    if (isMobile) {
      setisLeftDrawerOpen(false);
    }
  };

  useEffect(() => {
    setLoadedPercentage(10);
    initializeMap();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return undefined;

    const matchesSelectedBucket = (timestamp) => {
      const date = new Date(timestamp);
      const selected = new Date(selectedYearEditing);
      if (Number.isNaN(date.getTime()) || Number.isNaN(selected.getTime())) return false;
      switch (globalState.numericTimeAggregationMethod) {
        case 'year': return date.getUTCFullYear() === selected.getUTCFullYear();
        case 'month': return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth();
        case 'day': return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth() && date.getUTCDate() === selected.getUTCDate();
        case 'hour': return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth() && date.getUTCDate() === selected.getUTCDate() && date.getUTCHours() === selected.getUTCHours();
        default: return timestamp === selectedYearEditing;
      }
    };

    const readSelectedProperty = (feature, property) => {
      if (!property) return '—';
      if (globalState.scenarioModelledViewEnabled) {
        const scenarioValue = polygonCustomLayerRef.current?.getFeatureValue?.(feature, property, selectedYearEditing);
        if (scenarioValue !== undefined && scenarioValue !== null) {
          return typeof scenarioValue === 'number'
            ? scenarioValue.toLocaleString(undefined, { maximumFractionDigits: 3 })
            : scenarioValue;
        }
      }
      const values = Object.entries(feature.feature_data_simple_timeseries?.[property] || {})
        .filter(([timestamp]) => matchesSelectedBucket(timestamp))
        .map(([, value]) => value)
        .filter((value) => value !== null && value !== undefined && value !== '');
      if (!values.length) return '—';
      if (typeof values[0] === 'string') return values[0];
      const total = values.filter((value) => typeof value === 'number' && Number.isFinite(value))
        .reduce((sum, value) => sum + value, 0);
      return Number.isFinite(total) ? total.toLocaleString(undefined, { maximumFractionDigits: 3 }) : '—';
    };

    const displayName = (key) =>
      globalState.mapMetaData?.define_feature_data_simple_timeseries?.[key]?.name || key || 'Not selected';

    let pendingHoverEvent = null;
    let hoverFrame = null;
    const updateHoveredFeature = () => {
      hoverFrame = null;
      const event = pendingHoverEvent;
      if (!event) return;
      const feature = polygonCustomLayerRef.current?.getFeatureAtPoint(event.point);
      const hoveredId = feature?.skylark_id;
      if (hoveredId === undefined || hoveredId === null) {
        setHoveredFeatureTooltip(null);
        return;
      }

      const tooltip = hoverTooltipElementRef.current;
      if (tooltip) {
        tooltip.style.left = `${event.originalEvent.clientX + 12}px`;
        tooltip.style.top = `${event.originalEvent.clientY + 16}px`;
      }

      setHoveredFeatureTooltip((current) => {
        if (String(current?.id) === String(hoveredId)) return current;
        const scenarioChange = globalState.scenarioModelledViewEnabled
          ? polygonCustomLayerRef.current?.getScenarioChangeForFeature?.(feature, selectedYearEditing)
          : null;
        const featureArea = feature?.geometry ? turf.area(feature) : null;
        const featureColor = [fmesh, fmeshPoints]
          .flatMap((mesh) => mesh?.children || [])
          .find((child) => child.name === hoveredId && child.material?.color)
          ?.material.color.getHexString();
        return {
          id: hoveredId,
          displayName: feature?.feature_data_simple?.shortname || hoveredId,
          geometryType: feature?.geometry?.type,
          geometryColor: featureColor ? `#${featureColor}` : '#888',
          area: Number.isFinite(featureArea) ? `${(featureArea / 10000).toLocaleString(undefined, { maximumFractionDigits: 2 })} ha` : null,
          valueName: displayName(selectedMetric),
          value: readSelectedProperty(feature, selectedMetric),
          categoryName: displayName(selectedKey),
          category: readSelectedProperty(feature, selectedKey),
          scenarioAction: scenarioChange?.newLandtype,
          x: event.originalEvent.clientX + 14,
          y: event.originalEvent.clientY + 14,
        };
      });
    };

    const handleMapMouseMove = (event) => {
      pendingHoverEvent = event;
      if (hoverFrame === null) hoverFrame = requestAnimationFrame(updateHoveredFeature);
    };

    const handleMapMouseOut = () => {
      pendingHoverEvent = null;
      if (hoverFrame !== null) cancelAnimationFrame(hoverFrame);
      hoverFrame = null;
      setHoveredFeatureTooltip(null);
    };
    setHoveredFeatureTooltip(null);
    map.on('mousemove', handleMapMouseMove);
    map.on('mouseout', handleMapMouseOut);
    return () => {
      map.off('mousemove', handleMapMouseMove);
      map.off('mouseout', handleMapMouseOut);
      if (hoverFrame !== null) cancelAnimationFrame(hoverFrame);
    };
  }, [mapLoaded, selectedMetric, selectedKey, selectedYearEditing, globalState.numericTimeAggregationMethod, globalState.mapMetaData, globalState.scenarioModelledViewEnabled, globalState.scenarioModels]);

  const toastNotify = (message) => {
    toast(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      style: {
        background: "#29645D",
        color: "#FFFFFF",
        border: "2px solid #FFFFFF",
        fontWeight: "bold",
        borderRadius: "8px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
      },
      progressStyle: {
        background: "#07EA77",
        height: "20px",
      },
    });
  };

  toastRef.current = toastNotify;

  return (

    <div className={`home-theme${isDarkMode ? ' dark-mode' : ''}`}>

      <div className='top_menu'>
        <div className='title'>
          Logo
        </div>
        <div className='auth'>
          <MdAccountCircle />
        </div>
      </div>

      {/* Sighnpost to desktop version fullscreen overlay */}
      <MobileView>
        <div style={{ position: 'absolute', top: '0', left: '0', zIndex: '1000', width: '100%', height: '100%', backgroundColor: 'white' }}>
          <img src={logo} className='logo' alt='Skylark Logo' style={{ width: '100%', height: 'auto', position: 'relative', left: '50%', transform: 'translateX(-50%)', top: '200px' }} />
          <div style={{ width: '80%', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
            <h1 style={{ fontWeight: 'bold', fontSize: '1.8rem', color: '#000' }}>Please use a desktop device for full Functionality</h1>
          </div>
        </div>
      </MobileView>

      <LoadingSpinner
        isLoading={isLoading}
        loadedAmount={loadedAmount}
        setLoadedAmount={setLoadedAmount}
        setIsLoading={setIsLoading}
        loadedPercentage={loadedPercentage}
        globalState={globalState}
      />

      <TopMenu
        authClicked={authClicked}
        openAboutPanel={openAboutPanel}
        openPrivacyPanel={openPrivacyPanel}
        openTacPanel={openTacPanel}
        openInverstorsPanel={openInverstorsPanel}
        openMobileSignIn={openMobileSignIn}
        globalState={globalState}
        closeAuth={closeAuth}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode((dark) => !dark)}
      />

      <TopSubMenu
        loadMenuClick={loadMenuClick}
        isHasSaved={isHasSaved}
        isHasSavedCurrentData={isHasSavedCurrentData}
        loadedFileName={loadedFileName}
        isFilenameInputOpen={isFilenameInputOpen}
        newFilename={newFilename}
        handleFilenameInputChange={handleFilenameInputChange}
        isMenuLoadClick={isMenuLoadClick}
        closeLoadMenu={closeLoadMenu}
        filenames={filenames}
        mapboxTokenRef={mapboxTokenRef}
        globalRefs={globalRefs}
        globalState={globalState}
        setIsInAboutPanel={setIsInAboutPanel}
        setIsInInvestorsPanel={setIsInInvestorsPanel}
        setIsInPrivacyPanel={setIsInPrivacyPanel}
        setIsInTacPanel={setIsInTacPanel}
        isInAboutPanel={isInAboutPanel}
        isInInvestorsPanel={isInInvestorsPanel}
        isInPrivacyPanel={isInPrivacyPanel}
        isInTacPanel={isInTacPanel}
        resetAboutPanels={resetAboutPanels}
        isInSettings={isInSettings}
        mapbearing={mapbearing}
        loggedInToAPI={loggedInToAPI}
        universalParcelDifferentiator={universalParcelDifferentiator}
        ResetMap={ResetMap}
        setSelectedMetric={setSelectedMetric}
        universalDataLoaded={universalDataLoaded}
        isModifying={isModifying}
        setSelectedYearEditing={setSelectedYearEditing}
      />

      <LeftMenu
        isDrawing={isDrawing}
        isSelectingLandtypes={isSelectingLandtypes}
        LandtypeSelectionCancelOnClick={LandtypeSelectionCancelOnClick}
        setisSelectingLandtypes={setisSelectingLandtypes}
        beginDrawOnClick={beginDrawOnClick}
        endDrawOnClick={endDrawOnClick}
        endDrawCancelOnClick={endDrawCancelOnClick}
        toggleDrawer={toggleDrawer}
        isLeftDrawerOpen={isLeftDrawerOpen}
        isLeftSubDrawerOpen={isLeftSubDrawerOpen}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        PieChartselectionCancelOnClick={PieChartselectionCancelOnClick}
        ForecastCancelOnClick={ForecastCancelOnClick}
        ModifySelectionCancelOnClick={ModifySelectionCancelOnClick}
        selectedYearEditing={selectedYearEditing}
        mapbearing={mapbearing}
        setSelectedYearEditing={setSelectedYearEditing}
        isViewingPieChart={isViewingPieChart}
        isViewingForecast={isViewingForecast}
        isModifying={isModifying}
        handleModifyClick={handleModifyClick}
        isLanduse={isLanduse}
        handleLandUseClick={handleLandUseClick}
        isComparing={isComparing}
        handleCompareClick={handleCompareClick}
        globalState={globalState}
        globalRefs={globalRefs}
        loggedInToAPI={loggedInToAPI}
        ResetMap={ResetMap}
        longstandingGeoJSONRef={longstandingGeoJSONRef}
        selectedLandTypes={selectedLandTypes}
        handleLandtypePTagClick={handleLandtypePTagClick}
        universalParcelDifferentiator={universalParcelDifferentiator}
        universalColorPaletteName={universalColorPaletteName}
        landKeySelectedKey={landKeySelectedKey}
        setLandKeySelectedKey={setLandKeySelectedKey}
        polygonCustomLayerRef={polygonCustomLayerRef}
      />

      <LeftSubMenu
        isDrawing={isDrawing}
        beginDrawOnClick={beginDrawOnClick}
        endDrawOnClick={endDrawOnClick}
        endDrawCancelOnClick={endDrawCancelOnClick}
        isSelectingLandtypes={isSelectingLandtypes}
        toggleLeftSubDrawer={toggleLeftSubDrawer}
        isLeftSubDrawerOpen={isLeftSubDrawerOpen}
        handleLandtypePTagClick={handleLandtypePTagClick}
        selectedLandTypes={selectedLandTypes}
        isViewingPieChart={isViewingPieChart}
        longstandingGeoJSONRef={longstandingGeoJSONRef}
        originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
        isViewingForecast={isViewingForecast}
        selectedYearEditing={selectedYearEditing}
        setSelectedYearEditing={setSelectedYearEditing}
        ModifySelectionCancelOnClick={ModifySelectionCancelOnClick}
        isModifying={isModifying}
        isComparing={isComparing}
        handleModifyClick={handleModifyClick}
        handleCompareClick={handleCompareClick}
        selectedMetric={selectedMetric}
        uniqueTextureNamesArray={uniqueTextureNamesArray}
        handleLandUseClick={handleLandUseClick}
        isLanduse={isLanduse}
        polygonCustomLayerRef={polygonCustomLayerRef}
        isVisible={isVisible}
        dropdownhighlightedIndex={dropdownhighlightedIndex}
        setdropdownhighlightedIndex={setdropdownhighlightedIndex}
        setIsLanduse={setIsLanduse}
        piechartRef={piechartRef}
        handleEnlargePieButtonClick={handleEnlargePieButtonClick}
        setcomparingLandtypeSelectedOption={setcomparingLandtypeSelectedOption}
        resetLeftSubMenu={resetLeftSubMenu}
        setisLeftSubDrawerOpen={setisLeftSubDrawerOpen}
        storedfeaturecurrentlandtype={storedfeaturecurrentlandtype}
        setStoredFeatureCurrentLandtype={setStoredFeatureCurrentLandtype}
        temporaryGeoJSONIntendedForSaving={temporaryGeoJSONIntendedForSaving}
        setTemporaryGeoJSONIntendedForSaving={setTemporaryGeoJSONIntendedForSaving}
        setFinishedEditing={setFinishedEditing}
        finishedEditing={finishedEditing}
        universalParcelDifferentiator={universalParcelDifferentiator}
        universalColorPaletteName={universalColorPaletteName}
        hasChangedKeyValue={hasChangedKeyValue}
        setHasChangedKeyValue={setHasChangedKeyValue}
        compareSelectedKey={compareSelectedKey}
        setCompareSelectedKey={setCompareSelectedKey}
        landKeySelectedKey={landKeySelectedKey}
        setLandKeySelectedKey={setLandKeySelectedKey}
        universalDataLoaded={universalDataLoaded}
        handleLandtypePieHover={handleLandtypePieHover}
        activeLayer={activeLayer}
        PDFpiechartRef={PDFpiechartRef}
        globalState={globalState}
        globalRefs={globalRefs}
      />

      {/* <Login
        openAuth={openAuth}
        closeAuth={closeAuth}
        setIsInSettings={setIsInSettings}
        isInSettings={isInSettings}
        isInAboutPanel={isInAboutPanel}
        isInInvestorsPanel={isInInvestorsPanel}
        isInPrivacyPanel={isInPrivacyPanel}
        isInTacPanel={isInTacPanel}
        setIsInAboutPanel={setIsInAboutPanel}
        setIsInInvestorsPanel={setIsInInvestorsPanel}
        setIsInPrivacyPanel={setIsInPrivacyPanel}
        setIsInTacPanel={setIsInTacPanel}
        loggedInToAPI={loggedInToAPI}
        setLoggedInToAPI={setLoggedInToAPI}
        globalState={globalState}
      /> */}

      {/* <ClickedInfo
        isVisible={isVisible}
        polygonCustomLayerRef={polygonCustomLayerRef}
        clickedInfo={clickedInfo}
        clickedInfoHeader={clickedInfoHeader}
        clickedInfoHeaderMobile={clickedInfoHeaderMobile}
        globalState={globalState}
        globalRefs={globalRefs}
        foundFeature={foundFeature}
        longstandingGeoJSONRef={longstandingGeoJSONRef}
        originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
        ResetMap={ResetMap}
      /> */}

      <ClickedInfoSummary
        isVisible={isVisible}
        polygonCustomLayerRef={polygonCustomLayerRef}
        clickedInfo={clickedInfo}
        clickedInfoHeader={clickedInfoHeader}
        clickedInfoHeaderMobile={clickedInfoHeaderMobile}
        globalState={globalState}
        globalRefs={globalRefs}
        foundFeature={foundFeature}
        longstandingGeoJSONRef={longstandingGeoJSONRef}
        originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
        ResetMap={ResetMap}
      />

      {isComparing ? (
        <ComparePanel
          longstandingGeoJSONRef={longstandingGeoJSONRef}
          originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
          comparingLandtypeSelectedOption={comparingLandtypeSelectedOption}
          selectedYearEditing={selectedYearEditing}
          universalParcelDifferentiator={universalParcelDifferentiator}
          compareSelectedKey={compareSelectedKey}
          isVisible={isVisible}
          globalRefs={globalRefs}
          globalState={globalState}
        />
      ) : null}

      {isViewingLargePieChart && !isComparing ? (
        <LargePieChart
          geojson={longstandingGeoJSONRef.current}
          isViewingPieChart={isViewingPieChart}
          selectedMetric={selectedMetric}
          polygonCustomLayerRef={polygonCustomLayerRef}
          piechartRef={piechartRef}
          handleEnlargePieButtonClick={handleEnlargePieButtonClick}
          handleClosePieButtonClick={handleClosePieButtonClick}
          selectedYearEditing={selectedYearEditing}
          landKeySelectedKey={landKeySelectedKey}
        />
      ) : (
        null
      )}

      {isInSettings ? (
        <SettingsPanel
          isComparing={isComparing}
          handlesettingsClose={closeSettingsPanel}
          longstandingGeoJSONRef={longstandingGeoJSONRef}
          originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
          selectedMetric={selectedMetric}
          globalRefs={globalRefs}
          globalState={globalState}
        />
      ) : (
        null
      )}

      {isInAboutPanel ? (
        <AboutPanel
        />
      ) : (
        null
      )}

      {isInPrivacyPanel ? (
        <PrivacyPanel
        />
      ) : (
        null
      )}

      {isInTacPanel ? (
        <TacPanel
        />
      ) : (
        null
      )}

      {isInInvestorsPanel ? (
        <InvestorsPanel
        />
      ) : (
        null
      )}

      <DataLoad
        isMenuLoadClick={isMenuLoadClick}
        closeLoadMenu={closeLoadMenu}
        files={filenames}
        globalRefs={globalRefs}
        globalState={globalState}
        mapLoaded={mapLoaded}
        loggedInToAPI={loggedInToAPI}
        setLoggedInToAPI={setLoggedInToAPI}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        setLoadedPercentage={setLoadedPercentage}
        resetLeftSubMenu={resetLeftSubMenu}
      />

      <LoadMenuModal
        selectedFirstPrivateMapParentRef={selectedFirstPrivateMapParentRef}
        isMenuLoadClick={isMenuLoadClick}
        closeLoadMenu={closeLoadMenu}
        files={filenames}
        globalRefs={globalRefs}
        globalState={globalState}
        mapLoaded={mapLoaded}
        loggedInToAPI={loggedInToAPI}
        setLoggedInToAPI={setLoggedInToAPI}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        setLoadedPercentage={setLoadedPercentage}
        resetLeftSubMenu={resetLeftSubMenu}
        workingLocally={workingLocally}
        devDeployment={devDeployment}
      />

      <CreateAccountPopup
        globalRefs={globalRefs}
        globalState={globalState}
      />

      {!isVisible ? (
        // <TransparencySlider
        //   globalRefs={globalRefs}
        //   globalState={globalState}
        // />
        null
      ) : (
        null
      )}

      {isResetLandDataConfirmationModalOpen ? (
        <ResetLandDataConfirmationModal
          globalRefs={globalRefs}
          globalState={globalState}
        />
      ) : (
        null
      )}

      {isCreatingPDF ? (
        <PDFConverterModel
          globalRefs={globalRefs}
          globalState={globalState}
        />
      ) : (
        null
      )}

      {IsLoadingNewGeoJSONFromAnUpdate ? (
        <CommsToAPI
          message={APIReportMessage}
        />
      ) : (
        null
      )}

      {showFilterFeatureDataSimpleTimeseriesModal ? (
        <FilterFeatureDataSimpleTimeseriesModal
          globalRefs={globalRefs}
          globalState={globalState}
        />
      ) : (
        null
      )}

      {showFilterFeatureDataSimpleModal ? (
        <FilterFeatureDataSimpleModal
          globalRefs={globalRefs}
          globalState={globalState}
        />
      ) : (
        null
      )}

      <ToastContainer />

      <DynamicStringMetricKey
        globalState={globalState}
        globalRefs={globalRefs}
        longstandingGeoJSONRef={longstandingGeoJSONRef}
        polygonCustomLayerRef={polygonCustomLayerRef}
      />

      {/* <GlobalTimeseriesSlider
        selectedMetric={selectedMetric}
        isModifying={isModifying}
        selectedYearEditing={selectedYearEditing}
        globalState={globalState}
        globalRefs={globalRefs}
        setSelectedYearEditing={setSelectedYearEditing}
        ResetMap={ResetMap}
      /> */}

      <div ref={mapContainerRef} id='mapContainerRef' className='map-container'></div>

      {hoveredFeatureTooltip && (
        <div
          ref={hoverTooltipElementRef}
          className="map-feature-tooltip"
          style={{ left: `${hoveredFeatureTooltip.x}px`, top: `${hoveredFeatureTooltip.y}px` }}
        >
          <div className="map-feature-tooltip__header">
            {['Point', 'MultiPoint'].includes(hoveredFeatureTooltip.geometryType) ? (
              <GoDotFill aria-hidden="true" className="map-feature-tooltip__icon" style={{ color: hoveredFeatureTooltip.geometryColor }} />
            ) : ['LineString', 'MultiLineString'].includes(hoveredFeatureTooltip.geometryType) ? (
              <TbLine aria-hidden="true" className="map-feature-tooltip__icon" style={{ color: hoveredFeatureTooltip.geometryColor }} />
            ) : (
              <FaDrawPolygon aria-hidden="true" className="map-feature-tooltip__icon" style={{ color: hoveredFeatureTooltip.geometryColor }} />
            )}
            <div
              title={hoveredFeatureTooltip.displayName}
              className="map-feature-tooltip__title"
            >
              {hoveredFeatureTooltip.displayName}
            </div>
            {hoveredFeatureTooltip.area && (
              <div className="map-feature-tooltip__area">
                {hoveredFeatureTooltip.area}
              </div>
            )}
          </div>
          <div className="map-feature-tooltip__row">
            <span className="map-feature-tooltip__label">{hoveredFeatureTooltip.valueName}:</span>
            <span className="map-feature-tooltip__value" title={String(hoveredFeatureTooltip.value)}>{hoveredFeatureTooltip.value}</span>
          </div>
          <div className="map-feature-tooltip__row map-feature-tooltip__row--last">
            <span className="map-feature-tooltip__label">{hoveredFeatureTooltip.categoryName}:</span>
            <span className="map-feature-tooltip__value" title={String(hoveredFeatureTooltip.category)}>{hoveredFeatureTooltip.category}</span>
          </div>
          {hoveredFeatureTooltip.scenarioAction && (
            <div className="map-feature-tooltip__scenario">
              Scenario model: landtype → {hoveredFeatureTooltip.scenarioAction}
            </div>
          )}
        </div>
      )}

    </div>

  );

};

export default HomeApp;
